import { Box, Button, Stack } from '@mui/material';
import React, { Fragment } from 'react';
import { useNavigate } from 'react-router-dom';

import { CTAButtonFixed, MainFooter } from 'presentation/components';
import { Filter } from 'presentation/components/Filter';
import { RefundEnum } from 'presentation/constants';
import { translations } from 'presentation/translations-new';

import {
	useAccountInformation,
	useMediaQueries,
	useQueryParams,
} from 'presentation/pages/private/hooks';
import { useConsultRefundV2 } from 'presentation/pages/private/refund/hooks';
import type { RefundFlowNavigationState } from 'presentation/pages/private/refund/types/refundFlowNavigationState';
import { ConsultRefundFilterDrawer } from '../ConsultRefundFilterDrawer';
import { ConsultRefundDateFilter } from '../ConsultRefundDateFilter';
import { RefundCardList } from '../RefundCardList';
import { FiltersContainer, SearchStrip, StyledFooterContainer, StyledStack } from './styles';

const { searchProtocolPlaceholder, requestNewRefund } =
	translations['pt-br'].privateFlow.consultRefund;

export function ListStep() {
	const navigate = useNavigate();
	const { SEARCH_PARAMS } = useQueryParams();
	const { isTablet } = useMediaQueries();
	const { isHolder } = useAccountInformation();
	const { query: searchQuery } = SEARCH_PARAMS;
	const {
		abortDebounce,
		clearSearchParams,
		consultEndDate,
		consultInitialDate,
		consultProtocolNumber,
		datePickerOpenRequest,
		debouncedQueryFunc,
		setDateRange,
	} = useConsultRefundV2();

	const hideDateAndNewRefundForProtocolSearch =
		Boolean(consultProtocolNumber.trim()) || Boolean(searchQuery.trim());

	const showDateFilter = !hideDateAndNewRefundForProtocolSearch;
	const showNewRefundCta = isHolder && showDateFilter;

	function handleRequestNewRefund() {
		if (!isHolder) return;

		navigate(RefundEnum.TERMS, {
			state: {
				refundTermsReturnTo: RefundEnum.CONSULT,
			} satisfies RefundFlowNavigationState,
		});
	}

	return (
		<Fragment>
			<SearchStrip>
				<Filter.Root>
					<Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
						<Box sx={{ flex: 1, minWidth: 0 }}>
							<Filter.Search
								placeholder={searchProtocolPlaceholder}
								handleChange={() => {
									abortDebounce();
									debouncedQueryFunc();
								}}
								clearSearch={clearSearchParams}
							/>
						</Box>
						<ConsultRefundFilterDrawer />
					</Stack>
				</Filter.Root>
			</SearchStrip>

			<StyledStack>
				{showDateFilter ? (
					<FiltersContainer>
						<ConsultRefundDateFilter
							datePickerOpenRequest={datePickerOpenRequest}
							firstDate={consultInitialDate}
							secondDate={consultEndDate}
							handleFilter={({ dtInicio, dtFim }) =>
								setDateRange({ start: dtInicio, end: dtFim })
							}
						/>
						{showNewRefundCta && !isTablet ? (
							<Button
								variant="contained"
								size="medium"
								onClick={handleRequestNewRefund}>
								{requestNewRefund}
							</Button>
						) : null}

					</FiltersContainer>
				) : null}

				<RefundCardList />
			</StyledStack>
			{showNewRefundCta && isTablet ? (
				<CTAButtonFixed.Root $isTablet>
					<CTAButtonFixed.Action
						fullWidth
						variant="contained"
						size="large"
						onClick={handleRequestNewRefund}>
						{requestNewRefund}
					</CTAButtonFixed.Action>
				</CTAButtonFixed.Root>
			) : null}
			<StyledFooterContainer $mb={showNewRefundCta && isTablet ? 108 : undefined}>
				<MainFooter />
			</StyledFooterContainer>
		</Fragment>
	);
}
